import {
  InboxFilterState,
  SerializedFilters,
  FilterOptionsResponse,
} from "./types";

/**
 * Serializes inbox filter state to URL-friendly format
 * Uses | as separator for better readability (no URL encoding)
 */
export const serializeInboxFilters = (
  filters: InboxFilterState
): SerializedFilters => {
  const categories: string[] = [];
  if (filters.queriesEnabled) categories.push("queries");
  if (filters.leaveRequestsEnabled) categories.push("leave");

  return {
    ...(categories.length > 0 && { categories: categories.join("|") }),
    ...(filters.selectedQueryTypes.length > 0 && {
      queryTypes: filters.selectedQueryTypes.join("|"),
    }),
    ...(filters.selectedLeaveTypes.length > 0 && {
      leaveTypes: filters.selectedLeaveTypes.join("|"),
    }),
    ...(filters.selectedLeaveStatuses.length > 0 && {
      statuses: filters.selectedLeaveStatuses.join("|"),
    }),
  };
};

/**
 * Deserializes inbox filters from URL parameters
 * Uses | as separator and validates that filter combinations make logical sense
 */
export const deserializeInboxFilters = (
  serialized: SerializedFilters
): InboxFilterState => {
  const categories = serialized.categories?.split("|") || [];
  const queriesEnabled = categories.includes("queries");
  const leaveRequestsEnabled = categories.includes("leave");

  // Only include query types if queries are enabled
  const selectedQueryTypes = queriesEnabled
    ? serialized.queryTypes?.split("|").filter(Boolean) || []
    : [];

  // Only include leave types if leave requests are enabled
  const selectedLeaveTypes = leaveRequestsEnabled
    ? serialized.leaveTypes?.split("|").filter(Boolean) || []
    : [];

  // Only include statuses if leave requests are enabled
  const selectedLeaveStatuses = leaveRequestsEnabled
    ? serialized.statuses?.split("|").filter(Boolean) || []
    : [];

  return {
    queriesEnabled,
    leaveRequestsEnabled,
    selectedQueryTypes,
    selectedLeaveTypes,
    selectedLeaveStatuses,
  };
};

/**
 * Counts the number of active inbox filters for UI display
 * Only counts valid types and statuses, excludes categories
 */
export const getInboxActiveFilterCount = (
  filters: InboxFilterState,
  filterOptions?: FilterOptionsResponse
): number => {
  if (!filterOptions) {
    // Fallback: count without validation if options not available
    return (
      filters.selectedQueryTypes.length +
      filters.selectedLeaveTypes.length +
      filters.selectedLeaveStatuses.length
    );
  }

  // Get valid values for each category
  const validQueryTypeValues = filterOptions.query_types.map((qt) => qt.value);
  const validLeaveTypeValues = filterOptions.leave_types.map((lt) => lt.value);
  const validStatusValues = filterOptions.leave_statuses.map((ls) => ls.value);

  // Count only valid selections in each category
  const validQueryTypes = filters.selectedQueryTypes.filter((type) =>
    validQueryTypeValues.includes(type)
  );

  const validLeaveTypes = filters.selectedLeaveTypes.filter((type) =>
    validLeaveTypeValues.includes(type)
  );

  const validSelectedStatuses = filters.selectedLeaveStatuses.filter((status) =>
    validStatusValues.includes(status)
  );

  return (
    validQueryTypes.length +
    validLeaveTypes.length +
    validSelectedStatuses.length
  );
};

/**
 * Returns default inbox filter state
 */
export const getDefaultInboxFilters = (): InboxFilterState => ({
  queriesEnabled: true,
  leaveRequestsEnabled: true,
  selectedQueryTypes: [],
  selectedLeaveTypes: [],
  selectedLeaveStatuses: [],
});

/**
 * Checks if the current filter state is the default "Leave and Document" category selection
 * Default state means both categories enabled with no sub-filters selected
 */
export const isInDefaultFilterState = (filters: InboxFilterState): boolean => {
  const defaultFilters = getDefaultInboxFilters();

  return (
    filters.queriesEnabled === defaultFilters.queriesEnabled &&
    filters.leaveRequestsEnabled === defaultFilters.leaveRequestsEnabled &&
    filters.selectedQueryTypes.length ===
      defaultFilters.selectedQueryTypes.length &&
    filters.selectedLeaveTypes.length ===
      defaultFilters.selectedLeaveTypes.length &&
    filters.selectedLeaveStatuses.length ===
      defaultFilters.selectedLeaveStatuses.length
  );
};

/**
 * Cleans invalid filter values from state using filter options
 * Removes any types/statuses that don't exist in the current filter options
 */
export const cleanInboxFilters = (
  filters: InboxFilterState,
  filterOptions: FilterOptionsResponse
): InboxFilterState => {
  const validQueryTypeValues = filterOptions.query_types.map((qt) => qt.value);
  const validLeaveTypeValues = filterOptions.leave_types.map((lt) => lt.value);
  const validStatusValues = filterOptions.leave_statuses.map((ls) => ls.value);

  return {
    ...filters,
    selectedQueryTypes: filters.selectedQueryTypes.filter((type) =>
      validQueryTypeValues.includes(type)
    ),
    selectedLeaveTypes: filters.selectedLeaveTypes.filter((type) =>
      validLeaveTypeValues.includes(type)
    ),
    selectedLeaveStatuses: filters.selectedLeaveStatuses.filter((status) =>
      validStatusValues.includes(status)
    ),
  };
};
